import { API_BASE_URL } from "./config";
import { getToken, clearSession, rememberSignOutReason } from "./auth";
import type {
  ActiveSession,
  AssignmentProgress,
  BlockedDevice,
  DeviceRequest,
  AuthUser,
  BlockEvent,
  BlockStatus,
  ReopenRequest,
  DiscoveredCog,
  EditableLayerDetail,
  EditableLayerMerge,
  EditableLayerRows,
  EditableLayerSummary,
  FieldFeature,
  GeoJsonFeatureCollection,
  GeoJsonGeometry,
  HierarchyImportRecord,
  HierarchyImportResult,
  HierarchyLevel,
  NewUserPayload,
  Project,
  Raster,
  PropertyCounts,
  PropertyImportResult,
  ScopeGrant,
  SurveyBlock,
  SurveyTimingReport,
  UserSecurity,
  Ward,
  Zone,
} from "./types";

/** A failed API call, with a message fit to show as-is. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * What to tell the user about a failed response: the server's own explanation
 * when it sent one (FastAPI's `detail`, as a sentence or as {message}), else a
 * plain description of the status. Never the raw body -- an IIS error page or
 * a JSON blob is not something to put in front of anyone.
 */
export function explainFailure(status: number, body: string): string {
  try {
    const parsed = JSON.parse(body);
    const d = parsed?.detail;
    if (typeof d === "string" && d.trim()) return d;
    if (typeof d?.message === "string") return d.message;
    if (Array.isArray(d) && typeof d[0]?.msg === "string") {
      return `Some of the information sent was missing or in the wrong format: ${d[0].msg}.`;
    }
  } catch {
    // not JSON: fall through to the status
  }
  if (status === 400 || status === 422) return "The server couldn't accept this request: some of the information was missing or invalid.";
  if (status === 403) return "Your account doesn't have permission to do this. Ask an administrator if you need it.";
  if (status === 404) return "This item no longer exists -- it may have been deleted. Refresh the page and try again.";
  if (status === 409) return "This conflicts with a change made elsewhere. Refresh the page and try again.";
  if (status === 413) return "This file is too large for the server to accept.";
  if (status === 502 || status === 503 || status === 504) {
    return "The server isn't responding right now -- it may be restarting. Wait a minute and try again.";
  }
  if (status >= 500) return "Something went wrong on the server, so this wasn't completed. Try again; if it keeps happening, tell the administrator.";
  return `The request failed (status ${status}).`;
}

const UNREACHABLE =
  "Can't reach the server. Check the internet connection; if it is fine, the server may be down -- try again in a minute.";

/** fetch, with "no response at all" turned into a readable ApiError. */
async function send(url: string, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch {
    throw new ApiError(UNREACHABLE, 0);
  }
}

async function failure(res: Response): Promise<ApiError> {
  return new ApiError(explainFailure(res.status, await res.text().catch(() => "")), res.status);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken();
  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: {
        ...(init?.body && !(init.body instanceof FormData)
          ? { "Content-Type": "application/json" }
          : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
      cache: "no-store",
    });
  } catch {
    // fetch only rejects when no response arrived at all.
    throw new ApiError(UNREACHABLE, 0);
  }

  if (res.status === 401) {
    // Ended by the office (force logout, password change) or expired: the
    // server says which, and the login page shows it.
    rememberSignOutReason(await res.text().catch(() => ""));
    clearSession();
    if (typeof window !== "undefined") {
      window.location.href = "/login";
    }
    throw new Error("Session expired — please log in again.");
  }

  if (!res.ok) throw await failure(res);
  // A 204 (delete_raster's response, and any future one) has no body at
  // all -- res.json() on an empty body throws "Unexpected end of JSON
  // input", not a helpful error, just a crash on an otherwise-successful
  // request. Every other 2xx here does return a JSON body, so this only
  // takes the empty branch when there's genuinely nothing to parse.
  if (res.status === 204) return undefined as T;
  // The backend serves the portal itself and answers any path it doesn't know
  // with the portal's index.html (200). An API call landing there means the
  // running backend is older than this portal -- say so, instead of failing
  // with "Unexpected token '<'".
  if ((res.headers.get("content-type") ?? "").includes("text/html")) {
    throw new Error(
      `The backend does not have ${path.split("?")[0]} -- it is older than this portal. Restart it (stop.bat, then start.bat).`
    );
  }
  return res.json() as Promise<T>;
}

// --- Projects ---

export function listProjects(): Promise<Project[]> {
  return request<Project[]>("/projects");
}

export function getProject(projectId: number): Promise<Project> {
  return request<Project>(`/projects/${projectId}`);
}

export type ProjectStats = {
  zones: number;
  wards: number;
  blocks: number;
  by_status: {
    pending: number;
    in_progress: number;
    submitted: number;
  };
};

export function getProjectStats(projectId: number): Promise<ProjectStats> {
  return request<ProjectStats>(`/projects/${projectId}/stats`);
}

/** Properties surveyed (numbered) vs not started, project-wide and per ward / block. */
export type ProgressRow = {
  id: number;
  name: string;
  surveyed: number;
  not_started: number;
  zone_name?: string;
  ward_name?: string;
};
export type PropertyProgress = {
  total: number;
  surveyed: number;
  not_started: number;
  wards: ProgressRow[];
  blocks: ProgressRow[];
};

export function getPropertyProgress(projectId: number): Promise<PropertyProgress> {
  return request<PropertyProgress>(`/projects/${projectId}/property-progress`);
}

export type SurveyTimingFilter = {
  fromDate?: string;
  toDate?: string;
  zoneId?: number;
  wardId?: number;
};

export function getSurveyTiming(
  projectId: number,
  filters?: SurveyTimingFilter
): Promise<SurveyTimingReport> {
  const params = new URLSearchParams();
  if (filters?.fromDate) params.set("from_date", filters.fromDate);
  if (filters?.toDate) params.set("to_date", filters.toDate);
  if (filters?.zoneId != null) params.set("zone_id", String(filters.zoneId));
  if (filters?.wardId != null) params.set("ward_id", String(filters.wardId));
  const qs = params.toString();
  return request<SurveyTimingReport>(
    `/projects/${projectId}/survey-timing${qs ? `?${qs}` : ""}`
  );
}

export function createProject(payload: {
  name: string;
  description?: string;
}): Promise<Project> {
  return request<Project>("/projects", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/**
 * Deletes a project and everything beneath it -- every zone, ward, block,
 * field feature, and raster (DB rows and their S3 objects). Staff-only on
 * the backend, and by far the most destructive call in this file: unlike
 * deleteRaster (one file, re-uploadable) or deleteFeature (one shape), this
 * can remove an entire survey campaign's data in one request.
 */
export function deleteProject(projectId: number): Promise<void> {
  return request<void>(`/projects/${projectId}`, { method: "DELETE" });
}

// --- Taxation hierarchy: Zone -> Ward -> Block ---

export function listZones(projectId: number): Promise<Zone[]> {
  return request<Zone[]>(`/zones?project_id=${projectId}`);
}

export function createZone(payload: {
  project_id: number;
  name: string;
  boundary?: GeoJsonGeometry;
}): Promise<Zone> {
  return request<Zone>("/zones", { method: "POST", body: JSON.stringify(payload) });
}

export function getZone(zoneId: number): Promise<Zone> {
  return request<Zone>(`/zones/${zoneId}`);
}

/** What a hierarchy delete removed, or -- when blocked -- what it would have. */
export interface HierarchyDeleteSummary {
  blocks: number;
  /** Collected work: features carrying a house number. Imported outlines that
   *  nobody has numbered are counted separately and do NOT set
   *  has_survey_data -- re-importing the file recreates them, whereas a number
   *  a surveyor assigned in the field cannot be recovered. */
  features: number;
  imported_properties?: number;
  /** Set by an undo of a property import: outlines actually removed. */
  properties_removed?: number;
  rasters: number;
  worked_blocks: number;
  has_survey_data: boolean;
  wards?: number;
  zones?: number;
  message?: string;
}

export type HierarchyDeleteResult =
  | { blocked: false; summary: HierarchyDeleteSummary }
  | { blocked: true; summary: HierarchyDeleteSummary };

/**
 * Deletes a zone/ward/block and everything beneath it.
 *
 * Has its own fetch rather than going through request(): the backend answers 409
 * with a *structured* body describing the survey data that would be lost, and
 * request() would flatten that into a thrown error string. The caller needs the
 * numbers so it can ask the operator whether to proceed, so a blocked delete is
 * returned as a value, not raised.
 */
async function deleteHierarchyNode(
  path: string,
  force: boolean
): Promise<HierarchyDeleteResult> {
  const token = getToken();
  // The path may already carry a query string (the level-clear endpoint does),
  // so pick the right separator rather than always appending "?".
  const forceParam = force ? (path.includes("?") ? "&force=true" : "?force=true") : "";
  const res = await send(`${API_BASE_URL}${path}${forceParam}`, {
    method: "DELETE",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    cache: "no-store",
  });

  if (res.status === 401) {
    clearSession();
    if (typeof window !== "undefined") window.location.href = "/login";
    throw new Error("Session expired — please log in again.");
  }

  if (res.status === 409) {
    const body = (await res.json()) as { detail: HierarchyDeleteSummary };
    return { blocked: true, summary: body.detail };
  }

  if (!res.ok) throw await failure(res);

  return { blocked: false, summary: (await res.json()) as HierarchyDeleteSummary };
}

/** Project-wide totals per level. The clear action is project-wide, so the count
 *  shown next to it must be too -- the page's own lists are scoped to the current
 *  selection and would under-report (or read 0) whenever nothing is selected. */
export function getHierarchyCounts(
  projectId: number
): Promise<{ zone: number; ward: number; block: number }> {
  return request(`/projects/${projectId}/hierarchy-import`);
}

/**
 * Undoes a whole GeoJSON import: clears every zone, ward or block in the project.
 *
 * Deliberately the mirror image of importHierarchy, on the same backend path.
 * An import creates a whole level from one file, so the matching undo is
 * whole-level — removing rows one at a time was the wrong shape for the mistake,
 * since a 400-block file would take 400 clicks.
 *
 * Clearing a level also clears the levels beneath it (blocks reference wards,
 * wards reference zones, and none of those FKs is ON DELETE CASCADE). The
 * returned summary reports what was removed at every level so the caller can
 * state the true extent.
 */
export function clearHierarchyLevel(
  projectId: number,
  level: HierarchyLevel,
  force = false
): Promise<HierarchyDeleteResult> {
  return deleteHierarchyNode(
    `/projects/${projectId}/hierarchy-import?level=${level}`,
    force
  );
}

/** Uploads recorded for this project, newest first.
 *
 *  Only imports made since import tracking shipped are listed — anything
 *  created before has no record and can only be removed level-wide. */
export function listHierarchyImports(
  projectId: number
): Promise<HierarchyImportRecord[]> {
  return request<HierarchyImportRecord[]>(`/projects/${projectId}/hierarchy-imports`);
}

/**
 * Undoes one upload, leaving every other import in place.
 *
 * Goes through deleteHierarchyNode rather than request() for the same reason
 * the level-clear does: the backend answers 409 with a structured summary of
 * the field work that would be lost, and request() would flatten that into a
 * thrown string. A blocked undo is a value to act on, not an error.
 */
export function undoHierarchyImport(
  projectId: number,
  importId: number,
  force = false
): Promise<HierarchyDeleteResult> {
  return deleteHierarchyNode(
    `/projects/${projectId}/hierarchy-imports/${importId}`,
    force
  );
}

export function deleteZone(zoneId: number, force = false): Promise<HierarchyDeleteResult> {
  return deleteHierarchyNode(`/zones/${zoneId}`, force);
}

export function deleteWard(wardId: number, force = false): Promise<HierarchyDeleteResult> {
  return deleteHierarchyNode(`/wards/${wardId}`, force);
}

export function deleteBlock(blockId: number, force = false): Promise<HierarchyDeleteResult> {
  return deleteHierarchyNode(`/blocks/${blockId}`, force);
}

export function listWards(zoneId: number): Promise<Ward[]> {
  return request<Ward[]>(`/wards?zone_id=${zoneId}`);
}

export function createWard(payload: {
  zone_id: number;
  name: string;
  boundary?: GeoJsonGeometry;
}): Promise<Ward> {
  return request<Ward>("/wards", { method: "POST", body: JSON.stringify(payload) });
}

export function getWard(wardId: number): Promise<Ward> {
  return request<Ward>(`/wards/${wardId}`);
}

export function listBlocks(wardId: number): Promise<SurveyBlock[]> {
  return request<SurveyBlock[]>(`/blocks?ward_id=${wardId}`);
}

/** Every block of a zone in one call, instead of one call per ward. */
export function listBlocksInZone(zoneId: number): Promise<SurveyBlock[]> {
  return request<SurveyBlock[]>(`/blocks?zone_id=${zoneId}`);
}

export function createBlock(payload: {
  ward_id: number;
  name: string;
  boundary?: Record<string, unknown>;
}): Promise<SurveyBlock> {
  return request<SurveyBlock>("/blocks", { method: "POST", body: JSON.stringify(payload) });
}

export function getBlock(blockId: number): Promise<SurveyBlock> {
  return request<SurveyBlock>(`/blocks/${blockId}`);
}

/**
 * Sends a submitted block back for rework.
 *
 * Recorded on the server exactly like an approved reopen request: the reason
 * and how long the block stays open are required, and the signed-in account is
 * the approving authority. The block locks again by itself when it is
 * resubmitted or when `valid_until` passes.
 *
 * Staff/admin only — a surveyor asks instead (see listReopenRequests).
 */
export function reopenBlock(
  blockId: number,
  payload: { reason: string; valid_until: string; user_id?: number }
): Promise<{
  id: number;
  status: BlockStatus;
  reopened_by: string;
  valid_until: string | null;
  request_id: number | null;
}> {
  return request(`/blocks/${blockId}/reopen`, { method: "POST", body: JSON.stringify(payload) });
}

/** Ends the current claim. Survey data already synced stays on the block. */
export function releaseBlock(blockId: number): Promise<SurveyBlock> {
  return request<SurveyBlock>(`/blocks/${blockId}/release`, { method: "POST" });
}

/** Ends a pairing of two blocks. Claims and shapes stay as they are. */
export function endBlockPair(pairId: number): Promise<void> {
  return request<void>(`/blocks/pairs/${pairId}`, { method: "DELETE" });
}

/** Hands the block to another surveyor. Survey data already synced stays. */
export function reassignBlock(blockId: number, userId: number): Promise<SurveyBlock> {
  return request<SurveyBlock>(`/blocks/${blockId}/reassign`, {
    method: "POST",
    body: JSON.stringify({ user_id: userId }),
  });
}

/** The block's history (claims, releases, submissions, reopens), newest first. */
export function listBlockEvents(blockId: number): Promise<BlockEvent[]> {
  return request<BlockEvent[]>(`/blocks/${blockId}/events`);
}

/** Reopen requests: the pending queue first, then recent decisions. */
export function listReopenRequests(params: { status?: string; projectId?: number } = {}): Promise<ReopenRequest[]> {
  const q = new URLSearchParams();
  if (params.status) q.set("status", params.status);
  if (params.projectId !== undefined) q.set("project_id", String(params.projectId));
  const qs = q.toString();
  return request<ReopenRequest[]>(`/reopen-requests${qs ? `?${qs}` : ""}`);
}

export function approveReopenRequest(
  id: number,
  payload: { valid_until: string; note?: string }
): Promise<ReopenRequest> {
  return request<ReopenRequest>(`/reopen-requests/${id}/approve`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function rejectReopenRequest(id: number, note: string): Promise<ReopenRequest> {
  return request<ReopenRequest>(`/reopen-requests/${id}/reject`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}

/**
 * Exactly what a surveyor's device receives for this block.
 *
 * Used in preference to `listRasters({ blockId })` for block detail, because
 * that endpoint filters strictly on `block_id` -- so a project-wide orthomosaic
 * (the normal case: one drone flight covers many blocks) showed as "0 imagery"
 * in the portal while the field app correctly offered it. Reading the same
 * manifest the app reads keeps the two views from disagreeing about what
 * imagery exists.
 */
export function getSurveyPackage(blockId: number): Promise<{
  block: SurveyBlock;
  rasters: Raster[];
  features: FieldFeature[];
}> {
  return request(`/blocks/${blockId}/survey-package`);
}

// --- Rasters (drone imagery) — project-scoped OR block-scoped ---

/**
 * Which slice of imagery/features a call is about: a whole project, or one
 * block. Exactly one field is set.
 *
 * Declared once and shared, because it appears on four functions plus
 * RasterPanel's props, and the two ids are no longer the same type -- projects
 * use IDENTITY integers while blocks kept UUIDs. Four hand-written copies of a
 * mixed-type shape is four chances to get one of them wrong.
 */
export interface DataScope {
  projectId?: number;
  blockId?: number;
}

export function listRasters(scope: DataScope): Promise<Raster[]> {
  const query = scope.blockId
    ? `block_id=${scope.blockId}`
    : `project_id=${scope.projectId}`;
  return request<Raster[]>(`/rasters?${query}`);
}

/**
 * Uploads a raster, reporting real byte-level progress.
 *
 * Uses XMLHttpRequest rather than the `request()` helper above, because
 * fetch() cannot report *upload* progress at all (there is no equivalent of
 * xhr.upload.onprogress; the streaming-request API that would allow it is not
 * usable here). That gap was the whole problem: an orthomosaic is routinely
 * several GB, and the panel could only show a static "Uploading …" for however
 * many minutes that took, which is indistinguishable from being stuck.
 *
 * `onProgress` receives 0-100, and only when the browser reports a total
 * length to measure against (`lengthComputable`).
 */
export function uploadRaster(
  scope: DataScope,
  file: File,
  onProgress?: (percent: number) => void
): Promise<Raster> {
  const form = new FormData();
  if (scope.blockId != null) form.append("block_id", String(scope.blockId));
  // `!= null` rather than a truthiness test: projectId is a number now, and a
  // plain `if (scope.projectId)` would silently drop id 0. IDENTITY seeds at 1
  // so 0 should never occur, but a scope check is the wrong place to rely on
  // that.
  if (scope.projectId != null) form.append("project_id", String(scope.projectId));
  form.append("file", file);

  return new Promise<Raster>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_BASE_URL}/rasters/upload`);

    const token = getToken();
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    // Deliberately no Content-Type: the browser must set it, so the multipart
    // boundary is generated correctly -- same reason request() skips it for
    // FormData bodies.

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress((e.loaded / e.total) * 100);
      };
    }

    xhr.onload = () => {
      // Mirrors request()'s 401 handling, so an expired session behaves the
      // same whether it is noticed on an upload or on any other call.
      if (xhr.status === 401) {
        clearSession();
        if (typeof window !== "undefined") window.location.href = "/login";
        reject(new Error("Session expired — please log in again."));
        return;
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new ApiError(explainFailure(xhr.status, xhr.responseText), xhr.status));
        return;
      }
      try {
        resolve(JSON.parse(xhr.responseText) as Raster);
      } catch {
        reject(new Error("Upload succeeded but the response could not be parsed."));
      }
    };

    xhr.onerror = () => reject(new Error("Upload failed — the server could not be reached."));
    xhr.onabort = () => reject(new Error("Upload cancelled."));

    xhr.send(form);
  });
}

/**
 * Lists already-converted `*_cog.tif` files sitting in this project's storage
 * folder on the server. Read-only; registers nothing. Works for any project --
 * the backend derives the folder from projectId.
 */
export function discoverCogs(projectId: number): Promise<DiscoveredCog[]> {
  return request<DiscoveredCog[]>(`/rasters/discover-cogs?project_id=${projectId}`);
}

/**
 * Registers one of those files as ready imagery, skipping conversion entirely.
 * The backend re-validates it first, so a file whose name ends in _cog.tif but
 * which isn't actually a usable COG is refused rather than added.
 */
export function registerCog(projectId: number, filename: string): Promise<Raster> {
  const form = new FormData();
  form.append("project_id", String(projectId));
  form.append("filename", filename);
  return request<Raster>("/rasters/register-cog", { method: "POST", body: form });
}

/**
 * Removes a raster permanently -- its DB row and its S3 objects. There was
 * previously no way to do this at all: a failed upload stayed in the list,
 * in every count that includes it, forever, with the only workaround being
 * to re-upload under a different filename and leave the broken one behind.
 * Staff-only on the backend (irreversible, and the imagery may be
 * confidential) -- a non-staff caller gets a 403, surfaced like any other
 * request error.
 */
export function deleteRaster(rasterId: number): Promise<void> {
  return request<void>(`/rasters/${rasterId}`, { method: "DELETE" });
}

/**
 * Re-queues COG conversion for a raster that failed, without re-uploading --
 * the raw file is already in S3, so only the conversion step needs to run
 * again. Only meaningful for status "failed"; the backend 400s otherwise.
 */
export function retryRaster(rasterId: number): Promise<Raster> {
  return request<Raster>(`/rasters/${rasterId}/retry`, { method: "POST" });
}

/**
 * Reads a ready raster's real-world extent in WGS84 lon/lat, for "zoom to
 * this imagery" -- fitBounds on the map needs lon/lat, but TiTiler's own
 * /cog/info returns bounds in the raster's *native* CRS (this project's test
 * data is UTM 43N, i.e. metres, which fitBounds cannot use directly).
 * tilejson.json is the one TiTiler endpoint that reprojects bounds to
 * WGS84 for exactly this purpose.
 *
 * Goes straight to TiTiler rather than through our own backend: the COG's
 * S3 location is already fully encoded in tile_url's `url` query param, and
 * TiTiler needs no auth, so there's no reason to add a backend round-trip
 * for this.
 */
export async function fetchRasterBounds(
  tileUrl: string
): Promise<[number, number, number, number] | null> {
  let cogUrl: string | null;
  let titilerOrigin: string;
  try {
    const parsed = new URL(tileUrl);
    cogUrl = parsed.searchParams.get("url");
    titilerOrigin = parsed.origin;
  } catch {
    return null;
  }
  if (!cogUrl) return null;

  const res = await send(
    `${titilerOrigin}/cog/WebMercatorQuad/tilejson.json?url=${encodeURIComponent(cogUrl)}`,
    { cache: "no-store" }
  );
  if (!res.ok) return null;

  const json = (await res.json()) as { bounds?: [number, number, number, number] };
  return json.bounds ?? null;
}

// --- Field features (GeoJSON) — project-scoped OR block-scoped ---

export function listFeatures(scope: DataScope): Promise<FieldFeature[]> {
  const query = scope.blockId
    ? `block_id=${scope.blockId}`
    : `project_id=${scope.projectId}`;
  return request<FieldFeature[]>(`/features?${query}`);
}

/**
 * `block_submission_reverted` is now always false, and is kept only because
 * this type declares it.
 *
 * It used to mean "this was the block's last shape, so the backend un-submitted
 * the block". That auto-revert is gone: submitting a block now LOCKS its survey
 * data, so deleting a feature out of a submitted block is refused with 409
 * rather than silently reverting the submission. Staff reopen a block
 * explicitly via reopenBlock() when a correction is genuinely needed.
 *
 * A 409 from this call therefore means "that block is submitted", not "server
 * error" -- surface the message rather than a generic failure.
 */
export function deleteFeature(
  featureId: number
): Promise<{ deleted: boolean; block_submission_reverted?: boolean }> {
  return request<{ deleted: boolean; block_submission_reverted?: boolean }>(
    `/features/${featureId}`,
    { method: "DELETE" }
  );
}

/**
 * Edits one collected shape's attributes.
 *
 * Every field is optional and only what is sent is changed -- the backend
 * treats an absent key as "leave it alone", which is what lets the attribute
 * table PATCH just the cells someone actually touched.
 *
 * Not staff-only: the same endpoint the field app uses, so anyone with access
 * to the block can correct a number or an attribute from the office too.
 * Three refusals are worth surfacing verbatim rather than as a generic error:
 * 409 (the house number collides with another in the block), 422 (a
 * block-scoped property left with no number at all), and the submitted-block
 * lock, which refuses any change until staff reopen the block.
 */
/** Marks a property for a revisit (or clears the mark). Staff only. */
export function setFeatureRevisit(featureId: number, revisit: boolean): Promise<FieldFeature> {
  return request<FieldFeature>(`/features/${featureId}/revisit`, {
    method: "POST",
    body: JSON.stringify({ revisit }),
  });
}

/** Zone / ward / block narrowing for the overlap & sliver check. */
export type HierarchyScope = { zoneId?: number; wardId?: number; blockId?: number };

export type QaOverlap = {
  a_id: number;
  b_id: number;
  a_label: string;
  b_label: string;
  area_m2: number;
  percent_of_smaller: number;
  geometry: GeoJsonGeometry | null;
};
export type QaSliver = {
  id: number;
  label: string;
  area_m2: number;
  thinness: number;
  reason: "tiny" | "thin";
  geometry: GeoJsonGeometry;
};
export type FeatureQa = {
  checked: number;
  overlap_count: number;
  sliver_count: number;
  overlaps: QaOverlap[];
  slivers: QaSliver[];
};

/** Overlapping and sliver property polygons. Staff only, read-only. */
export function getFeatureQa(projectId: number, scope: HierarchyScope = {}): Promise<FeatureQa> {
  const params = new URLSearchParams({ project_id: String(projectId) });
  if (scope.zoneId != null) params.set("zone_id", String(scope.zoneId));
  if (scope.wardId != null) params.set("ward_id", String(scope.wardId));
  if (scope.blockId != null) params.set("block_id", String(scope.blockId));
  return request<FeatureQa>(`/features/qa?${params}`);
}

export function updateFeature(
  featureId: number,
  payload: {
    property_no?: string | null;
    partition?: string | null;
    properties?: Record<string, unknown>;
    geometry?: GeoJsonGeometry;
  }
): Promise<FieldFeature> {
  return request<FieldFeature>(`/features/${featureId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

// --- Hierarchy import (GeoJSON -> zones / wards / blocks) ---

/**
 * Creates a project's zones, wards or blocks from a GeoJSON file, one level
 * per call.
 *
 * Order matters: wards attach to this project's zones and blocks to its
 * wards, so those must be imported first -- the backend 400s with that
 * message rather than silently creating orphans.
 *
 * Parents are matched geometrically (a ward belongs to the zone it sits
 * inside), so the files don't need any shared key between them, and a
 * projected file is reprojected using whatever CRS it declares.
 */
export function importHierarchy(
  projectId: number,
  level: HierarchyLevel,
  file: File,
  opts?: { nameProperty?: string; sourceCrs?: string }
): Promise<HierarchyImportResult> {
  const form = new FormData();
  form.append("level", level);
  form.append("file", file);
  if (opts?.nameProperty) form.append("name_property", opts.nameProperty);
  if (opts?.sourceCrs) form.append("source_crs", opts.sourceCrs);
  return request<HierarchyImportResult>(`/projects/${projectId}/hierarchy-import`, {
    method: "POST",
    body: form,
  });
}

// --- Correcting zone / ward / block shapes in place ---
// Export a level with its SQL ids, edit it in GIS, feed it back: the rows are
// UPDATED by id (or by parent + name when the id is gone), so the field data
// filed under them stays attached. See routers/hierarchy_shapes.py.

export type ShapeChange = {
  row_id: number;
  name: string;
  matched_by: string;
  reshaped: boolean;
  renamed_from: string | null;
  moved_from: string | null;
  moved_to: string | null;
  features_outside: number;
};

export type ShapeUpdateResult = {
  level: HierarchyLevel;
  dry_run: boolean;
  update_id: number | null;
  in_file: number;
  matched: number;
  reshaped: number;
  renamed: number;
  moved: number;
  unchanged: number;
  skipped: number;
  untouched: number;
  features_outside: number;
  /** Properties re-filed under the block whose new outline now contains them. */
  features_moved: number;
  reprojected: boolean;
  changes: ShapeChange[];
  skipped_features: { feature: number; name: string | null; reason: string }[];
  moved_features: {
    feature_id: number;
    property_no: string | null;
    from_block: string;
    to_block: string;
    to_submitted: boolean;
  }[];
};

export type ShapeUpdateRecord = {
  id: number;
  level: HierarchyLevel;
  filename: string | null;
  reshaped: number;
  renamed: number;
  moved: number;
  created_by: string | null;
  created_at: string | null;
  undone_at: string | null;
  undone_by: string | null;
};

/** Downloads one level as GeoJSON, named by the server ("Project - Blocks <date>.geojson"). */
export async function exportHierarchyGeojson(projectId: number, level: HierarchyLevel): Promise<void> {
  const token = getToken();
  const res = await send(`${API_BASE_URL}/projects/${projectId}/hierarchy-export?level=${level}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    cache: "no-store",
  });
  if (res.status === 401) {
    clearSession();
    if (typeof window !== "undefined") window.location.href = "/login";
    throw new Error("Session expired — please log in again.");
  }
  if (!res.ok) {
    throw new Error(`Export failed: ${(await failure(res)).message}`);
  }
  if ((res.headers.get("content-type") ?? "").includes("text/html")) {
    throw new Error(
      "The backend does not have /hierarchy-export -- it is older than this portal. Restart it (stop.bat, then start.bat)."
    );
  }
  const disposition = res.headers.get("content-disposition") ?? "";
  const encoded = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(disposition);
  let filename = `${level}s.geojson`;
  try {
    if (encoded) filename = decodeURIComponent(encoded[1].trim());
  } catch {
    /* keep the fallback name */
  }
  const url = URL.createObjectURL(await res.blob());
  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Dry run (nothing written) unless `apply` is true. */
export function updateHierarchyShapes(
  projectId: number,
  level: HierarchyLevel,
  file: File,
  apply: boolean
): Promise<ShapeUpdateResult> {
  const form = new FormData();
  form.append("level", level);
  form.append("file", file);
  form.append("dry_run", apply ? "false" : "true");
  return request<ShapeUpdateResult>(`/projects/${projectId}/hierarchy-update`, {
    method: "POST",
    body: form,
  });
}

export function listShapeUpdates(projectId: number): Promise<ShapeUpdateRecord[]> {
  return request<ShapeUpdateRecord[]>(`/projects/${projectId}/hierarchy-updates`);
}

export function undoShapeUpdate(projectId: number, updateId: number): Promise<ShapeUpdateRecord> {
  return request<ShapeUpdateRecord>(`/projects/${projectId}/hierarchy-updates/${updateId}/undo`, {
    method: "POST",
  });
}

// --- Property outlines (imported building footprints the surveyor numbers) ---
// Replaces reference layers. The difference is that these become real survey
// rows: a reference layer was drawn under the map and still had to be traced
// over by hand, whereas an imported outline IS the property, waiting only for
// its house number. See the backend's routers/property_import.py.

/** Uploads a GeoJSON of property outlines. Each is matched to the block that
 *  contains it, and outlines already present at the same location are skipped
 *  rather than duplicated -- so re-uploading a corrected file is safe. */
export function importProperties(
  projectId: number,
  file: File,
  opts?: { sourceCrs?: string }
): Promise<PropertyImportResult> {
  const form = new FormData();
  form.append("file", file);
  if (opts?.sourceCrs) form.append("source_crs", opts.sourceCrs);
  return request<PropertyImportResult>(`/projects/${projectId}/property-import`, {
    method: "POST",
    body: form,
  });
}

/** How many property outlines this project holds and how many are numbered --
 *  the progress the panel reports. */
export function getPropertyCounts(projectId: number): Promise<PropertyCounts> {
  return request<PropertyCounts>(`/projects/${projectId}/property-import`);
}

/**
 * Removes every property outline imported into this project.
 *
 * Scoped server-side to imported rows, not to every field feature in the
 * project: once a surveyor numbers an outline it is indistinguishable from a
 * shape they drew themselves, and this must not sweep those up.
 *
 * Goes through deleteHierarchyNode so a refusal comes back as a value carrying
 * the counts, rather than a thrown string -- the second confirmation needs the
 * numbers.
 */
export function clearProperties(
  projectId: number,
  force = false
): Promise<HierarchyDeleteResult> {
  return deleteHierarchyNode(`/projects/${projectId}/property-import`, force);
}

// --- Users (staff can create further staff/surveyor accounts) ---

export function createUser(payload: NewUserPayload): Promise<AuthUser> {
  return request<AuthUser>("/auth/register-as-staff", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function getCurrentUser(): Promise<AuthUser> {
  return request<AuthUser>("/auth/me");
}

/**
 * Every account on the system.
 *
 * Staff-only on the backend. Password hashes are never in the response.
 */
/**
 * How far along one account's assignment is.
 *
 * Aggregated server-side on purpose. The obvious client-side version -- walk
 * the account's grants, fetch each ward's blocks, count them here -- is one
 * N+1 per account on a page that lists all of them, against a database a
 * long way away. See the endpoint's own docstring.
 */
export function getUserProgress(userId: number): Promise<AssignmentProgress> {
  return request<AssignmentProgress>(`/auth/users/${userId}/progress`);
}

export function listUsers(): Promise<AuthUser[]> {
  return request<AuthUser[]>("/auth/users");
}

/**
 * Removes an account permanently.
 *
 * The backend refuses two cases with a 400 and an explanatory message: deleting
 * your own account, and deleting the last remaining staff account. Both are
 * surfaced to the user verbatim rather than swallowed, because "nothing
 * happened" on a delete button is worse than a reason.
 *
 * Collected survey shapes are NOT deleted with the account -- they record their
 * author as a name, not a foreign key.
 */
export function deleteUser(userId: number): Promise<void> {
  return request<void>(`/auth/users/${userId}`, { method: "DELETE" });
}

/**
 * Sets a new password for an account without needing the old one.
 *
 * This is the office resetting a surveyor's forgotten password. These accounts
 * have no email, so there is no self-service reset path -- without this, a
 * forgotten password meant deleting and recreating the account.
 */
/**
 * Replaces which projects an account may work in.
 *
 * PUT with the complete set, not add/remove: the portal edits this as one
 * checklist, so a declarative call cannot drift from what the operator saw.
 *
 * Refused with 400 for an admin target -- admin is never project-restricted.
 */
/**
 * What an account is assigned, at whatever level it was granted.
 *
 * Assignments made before block-level scoping existed come back as
 * project-level grants, so the tree renders old and new the same way and there
 * is no separate legacy view to remember.
 */
export function getUserScopes(userId: number): Promise<ScopeGrant[]> {
  return request<ScopeGrant[]>(`/auth/users/${userId}/scopes`);
}

/** Replaces an account's assignments with exactly this set. PUT, not POST: the
 *  body is the whole tree as the operator left it. */
export function setUserScopes(
  userId: number,
  scopes: ScopeGrant[]
): Promise<ScopeGrant[]> {
  return request<ScopeGrant[]>(`/auth/users/${userId}/scopes`, {
    method: "PUT",
    body: JSON.stringify({ scopes }),
  });
}

export function setUserProjects(
  userId: number,
  projectIds: number[]
): Promise<AuthUser> {
  return request<AuthUser>(`/auth/users/${userId}/projects`, {
    method: "PUT",
    body: JSON.stringify({ project_ids: projectIds }),
  });
}

export function resetUserPassword(userId: number, password: string): Promise<AuthUser> {
  return request<AuthUser>(`/auth/users/${userId}/password`, {
    method: "POST",
    body: JSON.stringify({ password }),
  });
}

// --- Phone & session control (see backend services/sessions.py) ---

export function getUserSecurity(userId: number): Promise<UserSecurity> {
  return request<UserSecurity>(`/auth/users/${userId}/security`);
}

/** Releases the account's registered phone and signs it out. */
export function resetUserDevice(userId: number, allowFinalSync: boolean): Promise<UserSecurity> {
  return request<UserSecurity>(`/auth/users/${userId}/device/reset`, {
    method: "POST",
    body: JSON.stringify({ allow_final_sync: allowFinalSync }),
  });
}

/** Signs the account out everywhere, effective on its next request. */
export function forceLogoutUser(userId: number, allowFinalSync: boolean): Promise<UserSecurity> {
  return request<UserSecurity>(`/auth/users/${userId}/sessions/end`, {
    method: "POST",
    body: JSON.stringify({ allow_final_sync: allowFinalSync }),
  });
}

export function unlockUser(userId: number): Promise<UserSecurity> {
  return request<UserSecurity>(`/auth/users/${userId}/unlock`, { method: "POST" });
}

/** Blocks the account's registered phone: refused on every account until unblocked. */
export function blockUserDevice(
  userId: number,
  allowFinalSync: boolean,
  reason?: string
): Promise<UserSecurity> {
  return request<UserSecurity>(`/auth/users/${userId}/device/block`, {
    method: "POST",
    body: JSON.stringify({ allow_final_sync: allowFinalSync, reason: reason || null }),
  });
}

export function listDeviceRequests(): Promise<DeviceRequest[]> {
  return request<DeviceRequest[]>("/auth/device-requests");
}

export function approveDeviceRequest(requestId: number): Promise<UserSecurity> {
  return request<UserSecurity>(`/auth/device-requests/${requestId}/approve`, {
    method: "POST",
  });
}

/** Turns a phone down; block=true also blocks it on every account. */
export function rejectDeviceRequest(requestId: number, block: boolean): Promise<UserSecurity> {
  return request<UserSecurity>(`/auth/device-requests/${requestId}/reject`, {
    method: "POST",
    body: JSON.stringify({ block }),
  });
}

export function listBlockedDevices(): Promise<BlockedDevice[]> {
  return request<BlockedDevice[]>("/auth/blocked-devices");
}

export function unblockDevice(blockId: number): Promise<void> {
  return request<void>(`/auth/blocked-devices/${blockId}/unblock`, { method: "POST" });
}

export function disableUser(userId: number, allowFinalSync: boolean): Promise<UserSecurity> {
  return request<UserSecurity>(`/auth/users/${userId}/disable`, {
    method: "POST",
    body: JSON.stringify({ allow_final_sync: allowFinalSync }),
  });
}

export function enableUser(userId: number): Promise<UserSecurity> {
  return request<UserSecurity>(`/auth/users/${userId}/enable`, { method: "POST" });
}

/** Every live session on every account. */
export function listActiveSessions(): Promise<ActiveSession[]> {
  return request<ActiveSession[]>("/auth/sessions");
}

/** Ends one session, leaving the account's others signed in. */
export function endSession(sessionId: number, allowFinalSync: boolean): Promise<void> {
  return request<void>(`/auth/sessions/${sessionId}/end`, {
    method: "POST",
    body: JSON.stringify({ allow_final_sync: allowFinalSync }),
  });
}

/**
 * Pulls the human-readable message out of a thrown request() error.
 *
 * request() formats failures as "<status> <statusText>: <raw body>", where the
 * body is FastAPI's {"detail": "..."}. Showing that whole string puts JSON
 * punctuation in front of the user; showing only the status hides the reason the
 * backend went to the trouble of explaining.
 */
export function errorDetail(err: unknown): string {
  const text = err instanceof Error ? err.message : String(err);
  const brace = text.indexOf("{");
  if (brace !== -1) {
    try {
      const parsed = JSON.parse(text.slice(brace));
      if (typeof parsed?.detail === "string") return parsed.detail;
      if (typeof parsed?.detail?.message === "string") return parsed.detail.message;
    } catch {
      // Not JSON after all -- fall through to the raw text.
    }
  }
  return text;
}

// --- Export ---

/**
 * Positions of an editable layer's shapes inside the filter bar's area -- the
 * same set as listEditableLayerRows -- so the map can draw only those.
 */
export async function getEditableLayerPositions(
  projectId: number,
  layerId: number,
  opts: { zoneId?: number | null; wardId?: number | null; blockId?: number | null }
): Promise<number[]> {
  const params = new URLSearchParams();
  if (opts.blockId != null) params.set("block_id", String(opts.blockId));
  else if (opts.wardId != null) params.set("ward_id", String(opts.wardId));
  else if (opts.zoneId != null) params.set("zone_id", String(opts.zoneId));
  const res = await request<{ positions: number[] }>(
    `/projects/${projectId}/editable-layers/${layerId}/positions?${params.toString()}`
  );
  return res.positions;
}

/**
 * One page of an editable layer's shapes, cut to the filter bar's area (the
 * most specific of block, ward, zone; the whole project when none).
 */
export async function listEditableLayerRows(
  projectId: number,
  layerId: number,
  opts: {
    zoneId?: number | null;
    wardId?: number | null;
    blockId?: number | null;
    q?: string;
    /** Match `q` against this attribute only; every attribute when absent. */
    qColumn?: string;
    /** Only shapes last changed in [updatedFrom, updatedTo) -- ISO times. */
    updatedFrom?: string | null;
    updatedTo?: string | null;
    /** Only shapes last changed by this user. */
    updatedBy?: string | null;
    offset?: number;
    limit?: number;
  } = {}
): Promise<EditableLayerRows> {
  const params = new URLSearchParams();
  if (opts.blockId != null) params.set("block_id", String(opts.blockId));
  else if (opts.wardId != null) params.set("ward_id", String(opts.wardId));
  else if (opts.zoneId != null) params.set("zone_id", String(opts.zoneId));
  if (opts.q) params.set("q", opts.q);
  if (opts.q && opts.qColumn) params.set("q_column", opts.qColumn);
  if (opts.updatedFrom) params.set("updated_from", opts.updatedFrom);
  if (opts.updatedTo) params.set("updated_to", opts.updatedTo);
  if (opts.updatedBy) params.set("updated_by", opts.updatedBy);
  params.set("offset", String(opts.offset ?? 0));
  params.set("limit", String(opts.limit ?? 100));
  return request<EditableLayerRows>(
    `/projects/${projectId}/editable-layers/${layerId}/rows?${params.toString()}`
  );
}

/**
 * Downloads the Shapefile export.
 *
 * This used to be a plain `<a href={shapefileExportUrl(...)}>`. That could only
 * ever return 401: `/export/shapefile` sits behind `Depends(get_current_user)`,
 * and a browser navigation carries no Authorization header (the token lives in
 * localStorage, not a cookie). The Export button was therefore dead on arrival.
 *
 * Fetching it with the auth header and saving the response as a Blob is the fix.
 * The object URL is revoked afterwards so the downloaded archive -- which can be
 * large, and holds confidential survey data -- isn't pinned in memory for the
 * lifetime of the tab.
 */
export async function downloadShapefile(scope: DataScope & {
  /** Narrow a project export to one zone or ward (a block uses blockId). */
  zoneId?: number;
  wardId?: number;
}): Promise<void> {
  let query = scope.blockId
    ? `block_id=${scope.blockId}`
    : `project_id=${scope.projectId}`;
  if (!scope.blockId) {
    if (scope.wardId != null) query += `&ward_id=${scope.wardId}`;
    else if (scope.zoneId != null) query += `&zone_id=${scope.zoneId}`;
  }

  const token = getToken();
  const res = await send(`${API_BASE_URL}/export/shapefile?${query}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    cache: "no-store",
  });

  if (res.status === 401) {
    clearSession();
    if (typeof window !== "undefined") window.location.href = "/login";
    throw new Error("Session expired — please log in again.");
  }
  if (res.status === 404) {
    throw new Error("Nothing to export yet — no shapes have been collected here.");
  }
  if (!res.ok) {
    throw new Error(`Export failed: ${(await failure(res)).message}`);
  }

  // Prefer the filename the server chose, so exports stay traceable.
  const disposition = res.headers.get("content-disposition") ?? "";
  // "Project - Ward - Block DD-MM-YYYY HH-MM.zip". A name with non-English
  // characters (e.g. a Marathi ward name) arrives percent-encoded in filename*.
  const encoded = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(disposition);
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  let filename = `geosurvey-export-${scope.blockId ?? scope.projectId}.zip`;
  try {
    if (encoded) filename = decodeURIComponent(encoded[1].trim());
    else if (plain) filename = plain[1];
  } catch {
    if (plain) filename = plain[1];
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

// --- Editable layers ---
// Standalone GeoJSON overlays, separate from the survey hierarchy and from
// field data. Routes are nested under /projects because that prefix is already
// forwarded by both reverse proxies (Vite's dev proxy and IIS+ARR); a
// top-level /editable-layers would work locally and 404 in production until
// both configs were updated.

/** Every layer on a project, WITHOUT geometry -- names, counts and extents. */
export function listEditableLayers(projectId: number): Promise<EditableLayerSummary[]> {
  return request<EditableLayerSummary[]>(`/projects/${projectId}/editable-layers`);
}

/** One layer including its FeatureCollection, for drawing and editing. */
export function getEditableLayer(
  projectId: number,
  layerId: number
): Promise<EditableLayerDetail> {
  return request<EditableLayerDetail>(`/projects/${projectId}/editable-layers/${layerId}`);
}

/** Uploads a GeoJSON file as a new layer, named after the file. Reprojected
 *  server-side if it declares a CRS, same as the hierarchy import. */
export function uploadEditableLayer(
  projectId: number,
  file: File,
  opts?: { sourceCrs?: string }
): Promise<EditableLayerDetail> {
  const form = new FormData();
  form.append("file", file);
  if (opts?.sourceCrs) form.append("source_crs", opts.sourceCrs);
  return request<EditableLayerDetail>(`/projects/${projectId}/editable-layers`, {
    method: "POST",
    body: form,
  });
}

/**
 * Updates a layer from a new version of its file: new shapes added, existing
 * ones skipped, reshaped ones (matched by `matchKey`, e.g. OBJECTID) given
 * their new outline. Without `apply` it only reports what would happen.
 */
export function mergeEditableLayerFile(
  projectId: number,
  layerId: number,
  file: File,
  opts: { matchKey?: string | null; apply?: boolean }
): Promise<EditableLayerMerge> {
  const form = new FormData();
  form.append("file", file);
  if (opts.matchKey) form.append("match_key", opts.matchKey);
  form.append("apply", opts.apply ? "true" : "false");
  return request<EditableLayerMerge>(`/projects/${projectId}/editable-layers/${layerId}/merge`, {
    method: "POST",
    body: form,
  });
}

/** Replaces a layer's geometry with the edited collection. */
export function saveEditableLayer(
  projectId: number,
  layerId: number,
  geojson: GeoJsonFeatureCollection
): Promise<EditableLayerDetail> {
  return request<EditableLayerDetail>(`/projects/${projectId}/editable-layers/${layerId}`, {
    method: "PUT",
    body: JSON.stringify({ geojson }),
  });
}

export function deleteEditableLayer(projectId: number, layerId: number): Promise<void> {
  return request<void>(`/projects/${projectId}/editable-layers/${layerId}`, {
    method: "DELETE",
  });
}
